import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import RfPageFrame from "../layout/RfPageFrame";
import { allResources } from "../data/resourcesData";

const pillLabel = {
  news: "NEWS",
  whitepaper: "WHITEPAPER & REPORTS",
  article: "ARTICLES",
};

export default function ResourceArticle() {
  const { slug } = useParams();
  const article = allResources.find((r) => r.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="bg-white min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-slate-500 text-lg">Article not found.</p>
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-[#005a9c] font-semibold hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-hidden bg-white">
      {/* Hero */}
      {article.image && (
        <div className="relative h-72 md:h-96 w-full overflow-hidden bg-slate-100">
          <img
            src={article.image}
            alt={article.title}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <span className="inline-flex items-center rounded border border-white/30 bg-white/10 backdrop-blur-sm text-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.20em] mb-4">
              {pillLabel[article.type] || article.type}
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-white leading-snug max-w-3xl">
              {article.title}
            </h1>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="bg-[#f0f7ff] border-b border-[#e5eef6] py-12 md:py-16">
        <RfPageFrame className="!py-0">
          {/* Back link + meta */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#005a9c] hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Resources
            </Link>
            <div className="flex items-center gap-4 text-xs text-slate-400 font-medium">
              {article.author && <span>{article.author}</span>}
              {article.date && <span>· {article.date}</span>}
            </div>
          </div>

          {/* Article content */}
          <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-100 shadow-sm p-8 md:p-12 space-y-8">
            {/* Lead description */}
            <p className="text-[1.05rem] text-slate-600 leading-relaxed border-l-4 border-[#005a9c] pl-5 italic">
              {article.description}
            </p>

            {/* Sections */}
            {article.sections && article.sections.map((section, i) => (
              <div key={i} className="space-y-3">
                {section.heading && (
                  <h2 className="text-xl font-bold text-[#2c2c2c] leading-snug">
                    {section.heading}
                  </h2>
                )}
                {section.body && section.body.split("\n\n").map((para, j) => (
                  <p key={j} className="text-[0.95rem] leading-relaxed text-slate-700">
                    {para}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </RfPageFrame>
      </div>
    </div>
  );
}
